import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Receipt } from "lucide-react";
import { paidAccessApi, type PaidResourceListItem } from "@/lib/api/paid-access";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty-state";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import {
  paidResourceStateBadgeClass,
  paidResourceStateLabel,
} from "@/pages/patron/paid-resource-state";

export default function PaidResourcesPage() {
  const [items, setItems] = useState<PaidResourceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    paidAccessApi
      .listResources()
      .then(setItems)
      .catch(() => setError("Could not load paid resources."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Paid Resources"
        description="Materials available for your patron category."
        showSearch={false}
        breadcrumbItems={[{ label: "Dashboard", href: "/dashboard" }, { label: "Paid Resources" }]}
      />

      {loading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!loading && !error && items.length === 0 && (
        <Card className="border border-gray-200">
          <CardContent className="p-0">
            <EmptyState
              icon={<Receipt className="h-6 w-6 text-slate-400" />}
              title="No paid resources available"
              description="When the library publishes paid digital materials for your patron type, they will appear here."
            />
          </CardContent>
        </Card>
      )}

      {!loading && items.length > 0 && (
        <ul className="space-y-2">
          {items.map((item) => (
            <li key={item.id}>
              <Card className="border border-gray-200 transition-colors hover:border-primary/30">
                <Link
                  to={`/paid-resources/${item.id}`}
                  className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-primary">{item.title}</p>
                    {item.description && (
                      <p className="mt-1 line-clamp-2 text-sm text-slate-500">{item.description}</p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <Badge
                      variant="outline"
                      className={cn("border", paidResourceStateBadgeClass(item.derivedState))}
                    >
                      {paidResourceStateLabel(item.derivedState)}
                    </Badge>
                    <span className="text-sm font-semibold text-slate-900 tabular-nums">
                      {item.price <= 0 ? "Free" : `${item.currency} ${item.price.toLocaleString()}`}
                    </span>
                    <ChevronRight className="h-4 w-4 text-slate-400" aria-hidden />
                  </div>
                </Link>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
